import { Box } from '@mui/material'
import { getDeviationAnalysisExportCopy, getDeviationAnalysisWidgetCopy, tokens } from 'ComponentLibrary'
import { AnnotatedDeviationAnalysisWidget, type TextBubbleCopy } from 'ComponentLibrary/src/widgets/Annotations'
import { DeviationAnnotation } from '@einwert/valuation-api-client'
import { useValuationPlausibility } from './ValuationPlausibility.data'
import { useTranslation } from 'react-i18next'
import type { UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type PlausibilityDeviationAnalysisProps = {
    selectedValuationId?: string
    deviationAnnotations: DeviationAnnotation[]
    isImmediatelyVisible?: boolean
    hasMultipleDrafts: boolean
    hasNewerDraft: boolean
    filterOlderAnnotations: boolean
    textBubbleCopy: TextBubbleCopy
    userLinkCopy: Partial<UserLinkCopy>
}

export default function PlausibilityDeviationAnalysis({
    selectedValuationId,
    deviationAnnotations,
    isImmediatelyVisible,
    hasMultipleDrafts,
    hasNewerDraft,
    filterOlderAnnotations,
    textBubbleCopy,
    userLinkCopy,
}: PlausibilityDeviationAnalysisProps) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tDeviationAnalysisWidget } = useTranslation('terminology', { keyPrefix: 'common.deviationAnalysisWidget' })
    const { t: tExport } = useTranslation('terminology', { keyPrefix: 'export' })
    const locale = useCurrentResolvedLocale()
    const {
        addAnnotation,
        addReply,
        editAnnotation,
        editReply,
        deleteAnnotation,
        deleteReply,
        currentUser,
        isAddAnnotationPending,
        isEditAnnotationPending,
        isDeleteAnnotationPending,
        isValuationPlausibilityPending,
        deviationAnalysis,
        baseValuation,
        comparisonValuation,
    } = useValuationPlausibility()
    const deviationAnalysisExportCopy = getDeviationAnalysisExportCopy(tCommon, tExport)
    const deviationAnalysisWidgetCopy = getDeviationAnalysisWidgetCopy(
        deviationAnalysisExportCopy,
        tDeviationAnalysisWidget,
        comparisonValuation?.valuationDate ?? comparisonValuation?.date
    )

    return (
        <Box padding={tokens.SizingSpacing200} sx={{ overflow: 'auto', height: '100%' }}>
            <AnnotatedDeviationAnalysisWidget
                deviationAnalysis={deviationAnalysis}
                isDeviationAnalysisPending={isValuationPlausibilityPending}
                baseValuation={baseValuation}
                comparisonValuation={comparisonValuation}
                annotations={deviationAnnotations}
                addAnnotation={addAnnotation}
                addReply={addReply}
                editAnnotation={editAnnotation}
                editReply={editReply}
                deleteAnnotation={deleteAnnotation}
                deleteReply={deleteReply}
                currentUser={currentUser}
                isAddAnnotationPending={isAddAnnotationPending}
                isDeleteAnnotationPending={isDeleteAnnotationPending}
                isEditAnnotationPending={isEditAnnotationPending}
                isImmediatelyVisible={isImmediatelyVisible}
                hasMultipleDrafts={hasMultipleDrafts}
                draftId={selectedValuationId}
                hasNewerDraft={hasNewerDraft}
                filterOlderAnnotations={filterOlderAnnotations}
                locale={locale}
                copy={deviationAnalysisWidgetCopy}
                textBubbleCopy={textBubbleCopy}
                userLinkCopy={userLinkCopy}
            />
        </Box>
    )
}
